import { useEffect, useState } from 'react';
import { MapPin, Mail, Loader2, TrendingUp, Search } from 'lucide-react';
import { Input } from './ui/input';
import { hubAPI } from '../lib/api';
import { toast } from 'sonner';

const CoverageLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    hubAPI.coverageLeads()
      .then(res => setLeads(res.data || []))
      .catch(() => toast.error('Failed to load coverage leads'))
      .finally(() => setLoading(false));
  }, []);

  // Cluster leads by nearest_hub city
  const clusters = leads.reduce((acc, l) => {
    const key = l.nearest_hub || 'Unknown';
    if (!acc[key]) acc[key] = { hub: key, leads: [] };
    acc[key].leads.push(l);
    return acc;
  }, {});
  const clusterList = Object.values(clusters).sort((a, b) => b.leads.length - a.leads.length);

  const filtered = search.trim()
    ? leads.filter(l => (l.email || '').toLowerCase().includes(search.toLowerCase())
      || (l.address || '').toLowerCase().includes(search.toLowerCase())
      || (l.nearest_hub || '').toLowerCase().includes(search.toLowerCase()))
    : leads;

  return (
    <div className="space-y-4" data-testid="coverage-leads-manager">
      <div>
        <h3 className="font-semibold text-zinc-900">Coverage Expansion Leads</h3>
        <p className="text-xs text-zinc-500">
          People who searched for service in their area but were outside coverage. Use this to plan where to expand next.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-5 h-5 animate-spin text-orange-500" />
        </div>
      ) : leads.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-zinc-200">
          <Mail className="w-10 h-10 text-zinc-300 mx-auto mb-2" />
          <p className="text-zinc-600">No coverage leads yet.</p>
          <p className="text-xs text-zinc-400 mt-1">When out-of-area visitors leave their email, they'll appear here.</p>
        </div>
      ) : (
        <>
          {/* Cluster summary */}
          {clusterList.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {clusterList.slice(0, 6).map(c => (
                <div key={c.hub} className="bg-white border border-zinc-200 rounded-xl p-3" data-testid={`cluster-${c.hub.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-zinc-500">Closest to</p>
                    <TrendingUp className="w-3.5 h-3.5 text-orange-500" />
                  </div>
                  <p className="font-semibold text-zinc-900 truncate">{c.hub}</p>
                  <p className="text-2xl font-bold text-orange-600 mt-1">{c.leads.length}</p>
                  <p className="text-xs text-zinc-500">leads</p>
                </div>
              ))}
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-zinc-400" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by email, address, or hub..."
              className="pl-9"
              data-testid="coverage-search-input"
            />
          </div>

          {/* Lead table */}
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-xs text-zinc-500 uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">Email</th>
                    <th className="px-3 py-2 text-left">Address</th>
                    <th className="px-3 py-2 text-left">Nearest hub</th>
                    <th className="px-3 py-2 text-right">Distance</th>
                    <th className="px-3 py-2 text-left">Captured</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {filtered.map((l, idx) => (
                    <tr key={l.email || idx} className="hover:bg-zinc-50">
                      <td className="px-3 py-2 font-medium text-zinc-900">{l.email || '—'}</td>
                      <td className="px-3 py-2 text-zinc-700 truncate max-w-xs" title={l.address}>{l.address || '—'}</td>
                      <td className="px-3 py-2 text-zinc-700">
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-orange-500" />
                          {l.nearest_hub || '—'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-zinc-700">{l.distance_miles?.toFixed?.(1) ?? l.distance_miles ?? '—'} mi</td>
                      <td className="px-3 py-2 text-xs text-zinc-500">
                        {l.created_at ? new Date(l.created_at).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filtered.length === 0 && (
              <p className="p-4 text-center text-sm text-zinc-500">No leads match your search.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CoverageLeads;
